/**
 * @file Home.jsx
 * @description This component renders the main home page of the CITES-LEX application.
 * It includes an introduction, navigation buttons, informational content, a video player,
 * links to publications, and related resources. It also handles language-specific content
 * and video sources.
 */

import { FormattedMessage } from "react-intl";
import { useEffect, useState, useRef } from "react";
import Config from "../../config";
import { Link } from "react-router-dom";
import "./Home.css";
import ViewSpinner from "../../shared/Spinner";
import publ01 from "../../images/_home/Publ01.png";
import publ02 from "../../images/_home/Publ02.png";
import linkTranslations from "../../i18n/link_translation.json";

/**
 * The main functional component for the Home page.
 * @returns {JSX.Element} The rendered Home page.
 */
function Home() {
  
  // Public videos hosted on Google Cloud Storage
  const videoHomeEng = "https://storage.googleapis.com/citeslex-fe-static-files/videos/HomeVideoEng.mp4"
  const videoHomeFr = "https://storage.googleapis.com/citeslex-fe-static-files/videos/HomeVideoFr.mp4"
  const videoHomeEs = "https://storage.googleapis.com/citeslex-fe-static-files/videos/HomeVideoEs.mp4"
  
  const videoHowToEn = "https://storage.googleapis.com/citeslex-fe-static-files/videos/HowToEng.mp4"
  const videoHowToEs = "https://storage.googleapis.com/citeslex-fe-static-files/videos/HowToEs.mp4"
  const videoHowToFr = "https://storage.googleapis.com/citeslex-fe-static-files/videos/HowToFr.mp4"

  // State to store data for relevant/related websites, fetched from an API.
  const [relevant, setRelevant] = useState([]);
  // State to manage the loading status of the component, primarily for the initial data fetch.
  const [isLoading, setIsLoading] = useState(false);

  // State to control the visibility of the "How to Use" video modal.
  const [isOpen, setIsOpen] = useState(false);
  // Ref to access the main video player element directly.
  const videoRef = useRef(null);

  /**
   * Changes the source of the main video player and updates the selected language.
   * @param {string} newSrc - The new video source URL.
   * @param {string} lang - The language code corresponding to the new video ('en', 'fr', 'es').
   */
  const changeVideo = (newSrc, lang) => {
    setVideoSrc(newSrc);
    setSelectedLang(lang);
    if (videoRef.current) {
      videoRef.current.load(); // Force the video element to load the new source.
    }
  };

  /**
   * Opens the "How to Use" video modal.
   */
  const openVideo = () => {
    setIsOpen(true);
  };

  /**
   * Closes the "How to Use" video modal.
   */
  const closeVideo = () => {
    setIsOpen(false);
  };

  // Determines the current language from the URL path. Defaults to 'en'.
  var url = window.location.pathname.replace(".html", "");
  let dateLang = url.split("/")[1];
  if (
    dateLang !== "fr" &&
    dateLang !== "es" &&
    dateLang !== "ru" &&
    dateLang !== "ar" &&
    dateLang !== "zh"
  ) {
    dateLang = "en";
  }

  // State for the currently selected language for the main video.
  const [selectedLang, setSelectedLang] = useState(dateLang === "fr" ? "fr" : dateLang === "es" ? "es" : "en");
  // State for the source of the main introductory video.
  const [videoSrc, setVideoSrc] = useState(dateLang === "fr" ? videoHomeFr : dateLang === "es" ? videoHomeEs : videoHomeEng);
  // State for the source of the "How to Use" video.
  const [videoHowTo, setVideoHowTo] = useState(dateLang === "fr" ? videoHowToFr : dateLang === "es" ? videoHowToEs : videoHowToEn);

  /**
   * Retrieves a translated URL from the `link_translation.json` file.
   * @param {string} id - The identifier for the link.
   * @param {string} currentLang - The current language code.
   * @returns {string|null} The translated URL, the English URL as a fallback, or null if not found.
   */
  const getTranslatedLink = (id, currentLang) => {
    if (linkTranslations[id]) {
      if (linkTranslations[id][currentLang]) {
        return linkTranslations[id][currentLang];
      } else if (linkTranslations[id]['en']) {
        return linkTranslations[id]['en']; // Fallback to English if the specific language is not available.
      }
    }
    return null; // Return null if no link is found for the given id.
  };

  // Effect hook to fetch relevant websites data when the component mounts.
  useEffect(() => {
    setIsLoading(true);
    fetch(Config.apis.relevant)
      .then((response) => response.json())
      .then((data) => {
        setRelevant(data.related_websites);
      })
  }, []);

  return (
    <div>
      {/* Display a loading spinner until initial data is fetched. */}
      {isLoading ? (
        <div className="main">
          {/* Top section with background image and main navigation buttons */}
          <div className="home">
            <div className="color-background">
              <div className="card-title">
                <p style={{ color: "white" }}>
                  <FormattedMessage id="Home.intro" />
                </p>
              </div>
              <div className="button-wrap">
                <Link to={"/" + dateLang + "/search"} className="text-button">
                  <button className="button-main">
                    <i
                      id="asideToggleIcon"
                      className="bi bi-search inside-icon"
                    ></i>
                    <span className="ms-3">
                      <FormattedMessage id="Navigation.advanced" />
                    </span>
                  </button>
                </Link>

                <Link to="country" className="text-button">
                  <button className="button-main">
                    <i
                      id="asideToggleIcon"
                      className="bi bi-globe inside-icon"
                    ></i>
                    <span className="ms-3">
                      <FormattedMessage id="Navigation.country" />
                    </span>
                  </button>
                </Link>
              </div>
            </div>
          </div>
          {/* Main content section */}
          <section className="home-content">
            <div className="row mt-5">
              {/* Left column containing the main content */}
              <div className="col-9">
                <div className="row" style={{ alignItems: "center" }}>
                  {/* Video player section */}
                  <div className="col-5">
                    <div className="video-box">
                      <div style={{ marginTop: "10px" }}>
                        {/* Language selection buttons for the video */}
                        <div className="row" style={{ alignItems: "center" }}>
                          <div className="col-4">
                            <button className="button-main" 
                            style={{ 
                              width: "100%", 
                              textAlign: "center", 
                              backgroundColor: selectedLang === "en" || selectedLang === "ru" || selectedLang === "zh" || selectedLang === "ar" ? "#116AAB" : "white", 
                              color: selectedLang === "en" || selectedLang === "ru" || selectedLang === "zh" || selectedLang === "ar" ? "white" : "#545454" 
                            }} 
                            onClick={() => changeVideo(videoHomeEng, "en")}>Eng</button>
                          </div>
                          <div className="col-4">
                            <button className="button-main" style={{ width: "100%", textAlign: "center", backgroundColor: selectedLang === "fr" ? "#116AAB" : "white", color: selectedLang === "fr" ? "white" : "#545454", }} onClick={() => changeVideo(videoHomeFr, "fr")}>Fr</button>
                          </div>
                          <div className="col-4">
                            <button className="button-main" style={{ width: "100%", textAlign: "center", backgroundColor: selectedLang === "es" ? "#116AAB" : "white", color: selectedLang === "es" ? "white" : "#545454", }} onClick={() => changeVideo(videoHomeEs, "es")}>Es</button>
                          </div>
                        </div>



                      </div>
                      <video ref={videoRef} width="600" controls autoPlay muted>
                        <source src={videoSrc} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>
                  {/* Informational blue box section */}
                  <div className="col-7">
                    <div className="blue-box">
                      <div className="title-box">
                        <FormattedMessage id="Home.subtitle" />
                      </div>
                      <div className="text-box mt-2">
                        <FormattedMessage
                          id="Home.text_box1"
                          values={{
                            link: (chunks) => (
                              <a
                                href={getTranslatedLink("Home.text_box1", dateLang) || "https://cites.org/eng/disc/text.php"}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <span className="text-color">{chunks}</span>
                              </a>
                            ),
                          }}
                        />
                      </div>
                      <div className="text-box mt-3">
                        <FormattedMessage
                          id="Home.text_box2"
                          values={{
                            link: (chunks) => (
                              <a
                                href={getTranslatedLink("Home.text_box2", dateLang) || "https://cites.org/eng/app/index.php"}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <span className="text-color">{chunks}</span>
                              </a>
                            ),
                          }}
                        />
                      </div>
                      <div className="text-box mt-3" >
                        <FormattedMessage
                          id="Home.text_box3"
                          values={{
                            link: (chunks) => (
                              <a
                                href={getTranslatedLink("Home.text_box3", dateLang) || "https://cites.org/sites/default/files/projects/NLP/E-Model_law-revised_Oct.2021.FINAL.DRAFT.pdf"}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <span className="text-color">{chunks}</span>
                              </a>
                            ),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Additional informational text section */}
                <div className="row mt-5" >
                  <div className="d-flex justify-content-start align-items-start">

                    <div className="ms-5">
                      <div className="text-box">
                        <FormattedMessage
                          id="Home.text_box4"
                          values={{
                            bold: (chunks) => (
                              <span className="text-bold">{chunks}</span>
                            ),
                            p: (chunks) => <p>{chunks}</p>,
                            link1: (chunks) => (
                              <a
                                href={getTranslatedLink("Home.text_box4_link1", dateLang) || "https://cites.org/eng/meetings/cop"}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <span className="text-color">{chunks}</span>
                              </a>
                            ),
                            link2: (chunks) => (
                              <a
                                href={getTranslatedLink("Home.text_box4_link2", dateLang) || "https://cites.org/sites/default/files/documents/COP/19/resolution/E-Res-08-04-R15.pdf"}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <span className="text-color">{chunks}</span>
                              </a>
                            ),
                            link3: (chunks) => (
                              <a
                                href={getTranslatedLink("Home.text_box4_link3", dateLang) || "https://cites.org/sites/default/files/documents/COP/19/resolution/E-Res-18-07-R19.pdf"}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <span className="text-color">{chunks}</span>
                              </a>
                            ),
                            link4: (chunks) => (
                              <a
                                href={getTranslatedLink("Home.text_box4_link4", dateLang) || "https://cites.org/eng/legislation"}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <span className="text-color">{chunks}</span>
                              </a>
                            ),
                            link5: (chunks) => (
                              <a
                                href={getTranslatedLink("Home.text_box4_link5", dateLang) || "https://www.fao.org/faolex/en/"}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <span className="text-color">{chunks}</span>
                              </a>
                            ),
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="image-home">
                        <img
                          src="https://faolex.fao.org/docs/json/citeslex2.jpg"
                          alt="test"
                        ></img>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Publications section */}
                <div className="row mt-4">
                  <div className="col">
                    <div className="blue-light-box">
                      <div className="row">
                        <div className="col-3">
                          <img src={publ01} alt="publ01"></img>
                        </div>
                        <div className="col-9">
                          <div className="title-card-box">
                            <FormattedMessage id="Home.title_box_card1" />
                          </div>
                          <div className="text-card-box mt-3">
                            <FormattedMessage
                              id="Home.text_box_card1"
                              values={{
                                p: (chunks) => <p>{chunks}</p>,
                                en: (chunks) => (
                                  <a
                                    href="https://www.fao.org/documents/card/en/c/cc8051en"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <span className="text-color">{chunks}</span>
                                  </a>
                                ),
                                es: (chunks) => (
                                  <a
                                    href="https://www.fao.org/documents/card/en/c/CC8051ES"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <span className="text-color">{chunks}</span>
                                  </a>
                                ),
                                fr: (chunks) => (
                                  <a
                                    href="https://www.fao.org/documents/card/en/c/CC8051FR"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <span className="text-color">{chunks}</span>
                                  </a>
                                ),
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="blue-light-box">
                      <div className="row">
                        <div className="col-3">
                          <img src={publ02} alt="publ02"></img>
                        </div>
                        <div className="col-9">
                          <div className="title-card-box">
                            <FormattedMessage id="Home.title_box_card2" />
                          </div>
                          <div className="text-card-box mt-3">
                            <FormattedMessage
                              id="Home.text_box_card2"
                              values={{
                                list: (chunks) => <ul>{chunks}</ul>,
                                element: (chunks) => <li>{chunks}</li>,
                                link1: (chunks) => (
                                  <a
                                    href="https://www.fao.org/3/cc5219en/cc5219en.pdf"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <span className="text-color">{chunks}</span>
                                  </a>
                                ),
                                link2: (chunks) => (
                                  <a
                                    href="https://www.fao.org/3/cc5072en/cc5072en.pdf"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <span className="text-color">{chunks}</span>
                                  </a>
                                ),
                                link3: (chunks) => (
                                  <a
                                    href="https://www.fao.org/documents/card/es/c/cc7657es"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <span className="text-color">{chunks}</span>
                                  </a>
                                ),
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Final text section */}
                <div className="row mt-4">
                  <div className="col">
                    <div className="text-last">
                      <FormattedMessage id="Home.text_last" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Right column for sidebar content */}
              <div className="col-3">
                <div className="row">
                  {/* Methodology and How-to-Use buttons */}
                  <div className="col-6">
                    <div className="blue-title-box">
                      <Link to="https://faolex.fao.org/docs/json/citeslex_methodology.pdf" target="_blank">
                        <i className="bi bi-file-earmark-text me-2 white-text"></i>
                        <span className="white-text">
                          <FormattedMessage id="Home.methodology" />
                        </span>
                      </Link>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="blue-title-box" style={{ cursor: "pointer" }}>
                      <i className="bi bi-file-play me-2 white-text"></i>
                      <span className="white-text" onClick={() => openVideo()}>
                        <FormattedMessage id="Home.howtouse" />
                      </span>
                    </div>
                  </div>
                  {/* Video modal for "How to Use" */}
                  {isOpen && (
                    <div style={styles.overlay} onClick={closeVideo}>
                      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <span style={styles.close} onClick={closeVideo}>&times;</span>
                        <video width="100%" controls autoPlay>
                          <source src={videoHowTo} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    </div>
                  )}
                </div>
                <div className="title-box my-3">
                </div>
                {/* Relevant Websites section */}
                {relevant && relevant.length > 0
                  ? relevant.map((el) =>
                    el.category === 0 ? (
                      <div className="text-blue-box mb-2" key={el.id}>
                        <Link to={`${el[`url_${dateLang}`] || el.url_en}`} target="_blank">
                          <i className="bi bi-globe me-1"></i>
                          {el[`name_${dateLang}`] || el.name_en}
                        </Link>
                      </div>
                    ) : null
                  )
                  : null}
                {/* Related Databases section */}
                <div className="title-box my-3">
                  <FormattedMessage id="Home.related" />
                </div>
                {relevant && relevant.length > 0
                  ? relevant.map((el) =>
                    el.category === 1 ? (
                      <div className="text-blue-box mb-2" key={el.id}>
                        <Link to={`${el[`url_${dateLang}`] || el.url_en}`} target="_blank">
                          <i className="bi bi-globe me-1"></i>
                          {el[`name_${dateLang}`] || el.name_en}
                        </Link>
                      </div>
                    ) : null
                  )
                  : null}
                {/* CITES Resources section */}
                <div className="title-box mt-4 mb-3">
                  <FormattedMessage id="Home.resources" />
                </div>
                {relevant && relevant.length > 0
                  ? relevant.map((el) =>
                    el.category === 2 ? (
                      <div className="text-blue-box mb-2" key={el.id}>
                        <Link to={`${el[`url_${dateLang}`] || el.url_en}`} target="_blank">
                          <i className="bi bi-globe me-1"></i>
                          {el[`name_${dateLang}`] || el.name_en}
                        </Link>
                      </div>
                    ) : null
                  )
                  : null}
                {/* Contact Us box */}
                <div className="blue-contact-box mt-5">
                  <div className="contact-title">
                    <FormattedMessage id="Footer.contactus" />
                  </div>
                  <div className="contact-text mt-2">
                    <FormattedMessage
                      id="Home.contact_text"
                      values={{
                        color: (chunks) => (
                          <span className="text-color">{chunks}</span>
                        ),
                        link: (chunks) => (
                          <a
                            href={`mailto:${chunks}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <span className="text-color">{chunks}</span>
                          </a>
                        ),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : (
        // Loading spinner shown during initial data fetch
        <div className="loading">
          <div className="wrap-spinner">
            {" "}
            <ViewSpinner />{" "}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * @description Styles for the video modal overlay and content.
 * These are defined as a JavaScript object and applied inline.
 */
const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    position: "relative",
    width: "80%",
    maxWidth: "800px",
    backgroundColor: "#000",
    padding: "10px",
    borderRadius: "10px",
    boxShadow: "0px 0px 10px rgba(255, 255, 255, 0.2)",
  },
  close: {
    position: "absolute",
    top: "10px",
    right: "20px",
    fontSize: "30px",
    color: "#fff",
    cursor: "pointer",
  },
};

export default Home;
